import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Package, Plus, Search, Trash2, AlertCircle, ArrowUpDown,
  BarChart3, X, Save, AlertTriangle, TrendingUp, RefreshCw,
} from "lucide-react";
import ProductDetail from "../components/ProductDetail";

const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

const fmtNum = (v, digits = 2) =>
  new Intl.NumberFormat("ca-ES", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(Number(v) || 0);

const UNITS = ["u", "kg", "g", "L", "mL", "m", "m²", "m³", "h", "caixa", "paquet"];

const blank = () => ({
  code: "", name: "", unit: "u", category: "",
  default_supplier_nif: "", default_supplier_name: "",
  default_price: 0, min_stock: 0, notes: "",
  is_active: true,
});

export default function Products() {
  const { lang } = useI18n();
  const [list, setList] = useState([]);
  const [overview, setOverview] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/products", { params: { q: search, only_active: true, limit: 500 } }),
        api.get("/products/stats/overview"),
        api.get("/products/price-alerts", { params: { limit: 30 } }),
      ]);
      setList(r1.data.items || []);
      setOverview(r2.data || {});
      setAlerts(r3.data.alerts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(blank()); setShowDialog(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      code: p.code || "", name: p.name || "", unit: p.unit || "u",
      category: p.category || "",
      default_supplier_nif: p.default_supplier_nif || "",
      default_supplier_name: p.default_supplier_name || "",
      default_price: p.default_price || 0,
      min_stock: p.min_stock || 0, notes: p.notes || "",
      is_active: p.is_active !== false,
    });
    setShowDialog(true);
  };
  const closeDialog = () => { setShowDialog(false); setEditing(null); setForm(blank()); };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error(lang === "es" ? "El nombre es obligatorio" : "El nom és obligatori");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, form);
      } else {
        await api.post("/products", form);
      }
      toast.success(lang === "es" ? "Guardado" : "Desat");
      closeDialog();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "duplicate_code") {
        toast.error(lang === "es"
          ? `Ya existe un producto con código "${form.code}"`
          : `Ja existeix un producte amb codi "${form.code}"`);
      } else {
        toast.error(d || "Error");
      }
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(
      lang === "es" ? `Eliminar "${editing.name}"?` : `Eliminar "${editing.name}"?`
    )) return;
    try {
      await api.delete(`/products/${editing.id}`);
      toast.success(lang === "es" ? "Eliminado" : "Eliminat");
      closeDialog();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">
            {lang === "es" ? "Mercancías" : "Mercaderies"}
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {lang === "es"
              ? "Catálogo de artículos, stocks, historial de precios y comparativas entre proveedores"
              : "Catàleg d'articles, estocs, historial de preus i comparatives entre proveïdors"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={async () => {
              const ok = window.confirm(
                lang === "es"
                  ? "¿Reconstruir el catálogo desde las facturas validadas?\n\nEsto:\n• Recalcula items[] desde el OCR original\n• Borra movimientos antiguos de inventario\n• Vuelve a crearlos con la cantidad y precio efectivo correctos\n\nEs seguro (idempotente). Solo afecta a tu empresa."
                  : "Reconstruir el catàleg des de les factures validades?\n\nAixò:\n• Recalcula items[] des de l'OCR original\n• Esborra moviments antics d'inventari\n• Els torna a crear amb la quantitat i preu efectiu correctes\n\nÉs segur (idempotent). Només afecta la teva empresa."
              );
              if (!ok) return;
              const toastId = toast.loading(
                lang === "es" ? "Reconstruyendo inventario..." : "Reconstruint inventari..."
              );
              try {
                const { data } = await api.post("/received-invoices/rematerialize-all");
                toast.dismiss(toastId);
                toast.success(
                  lang === "es"
                    ? `${data.movements_created} movimientos creados · ${data.products_created} productos nuevos · ${data.items_rebuilt} facturas re-procesadas`
                    : `${data.movements_created} moviments creats · ${data.products_created} productes nous · ${data.items_rebuilt} factures re-processades`,
                  { duration: 6000 }
                );
                await load();
              } catch (e) {
                toast.dismiss(toastId);
                toast.error(e?.response?.data?.detail || "Error");
              }
            }}
            variant="outline"
            className="border-amber-500/30 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
            data-testid="products-rebuild-btn"
            title={lang === "es"
              ? "Re-genera el catálogo y los movimientos desde las facturas validadas con el nuevo parser"
              : "Re-genera el catàleg i els moviments des de les factures validades amb el nou parser"}
          >
            <RefreshCw size={14} className="mr-1.5" />
            {lang === "es" ? "Reconstruir desde facturas" : "Reconstruir des de factures"}
          </Button>
          <Button
            onClick={openNew}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="products-new-btn"
          >
            <Plus size={16} className="mr-2" />
            {lang === "es" ? "Nuevo artículo" : "Nou article"}
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          icon={<Package size={16} />}
          label={lang === "es" ? "Artículos" : "Articles"}
          value={overview.total_products || 0}
          tint="from-blue-500/15 to-blue-500/0"
        />
        <KpiCard
          icon={<AlertCircle size={16} />}
          label={lang === "es" ? "Bajo stock" : "Estoc baix"}
          value={overview.low_stock_count || 0}
          tint="from-rose-500/15 to-rose-500/0"
        />
        <KpiCard
          icon={<ArrowUpDown size={16} />}
          label={lang === "es" ? "Movimientos" : "Moviments"}
          value={overview.total_movements || 0}
          tint="from-emerald-500/15 to-emerald-500/0"
        />
        <KpiCard
          icon={<BarChart3 size={16} />}
          label={lang === "es" ? "Compres (uds.)" : "Compres (u.)"}
          value={fmtNum(overview.total_purchases_units || 0, 0)}
          tint="from-amber-500/15 to-amber-500/0"
        />
        <KpiCard
          icon={<AlertTriangle size={16} />}
          label={lang === "es" ? "Alertes preu" : "Alertes preu"}
          value={overview.price_alerts_count || 0}
          tint="from-amber-500/20 to-rose-500/0"
        />
      </div>

      {/* Price alerts banner */}
      <PriceAlertsBanner alerts={alerts} lang={lang} onOpen={(pid) => setDetailId(pid)} />

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === "es" ? "Buscar por código o nombre..." : "Cerca per codi o nom..."}
          className="pl-9 bg-white/5 border-white/10"
          data-testid="products-search-input"
        />
      </div>

      {/* Table */}
      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            {lang === "es" ? "Cargando..." : "Carregant..."}
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Package size={32} className="mx-auto mb-3 text-slate-600" />
            <p>{lang === "es" ? "Sin artículos. Crea el primero." : "Sense articles. Crea el primer."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-xs uppercase tracking-wider text-slate-400">
                  <th className="text-left p-3">{lang === "es" ? "Código" : "Codi"}</th>
                  <th className="text-left p-3">{lang === "es" ? "Nombre" : "Nom"}</th>
                  <th className="text-left p-3 hidden md:table-cell">{lang === "es" ? "Categoría" : "Categoria"}</th>
                  <th className="text-right p-3">{lang === "es" ? "Stock" : "Estoc"}</th>
                  <th className="text-right p-3 hidden sm:table-cell">{lang === "es" ? "Último precio" : "Últim preu"}</th>
                  <th className="text-right p-3 hidden lg:table-cell">{lang === "es" ? "Proveedor" : "Proveïdor"}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => {
                  const lowStock = p.min_stock > 0 && p.current_stock < p.min_stock;
                  return (
                    <tr
                      key={p.id}
                      className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => setDetailId(p.id)}
                      data-testid={`product-row-${p.id}`}
                    >
                      <td className="p-3 text-slate-300 font-mono text-xs">{p.code || "—"}</td>
                      <td className="p-3">
                        <div className="text-slate-200 font-medium">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.unit}</div>
                      </td>
                      <td className="p-3 text-slate-400 hidden md:table-cell">{p.category || "—"}</td>
                      <td className="p-3 text-right font-mono">
                        <span className={lowStock ? "text-rose-300 font-semibold" : "text-slate-200"}>
                          {fmtNum(p.current_stock, 2)}
                        </span>
                        {lowStock && (
                          <div className="text-[10px] text-rose-400 mt-0.5">
                            {lang === "es" ? "min." : "mín."} {fmtNum(p.min_stock, 0)}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono hidden sm:table-cell">
                        {p.last_price > 0 ? fmtEur(p.last_price) : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="p-3 text-slate-400 text-xs hidden lg:table-cell truncate max-w-[150px]">
                        {p.last_supplier_nif || p.default_supplier_name || "—"}
                      </td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openEdit(p)}
                          className="px-2 py-1 text-xs text-slate-400 hover:text-white rounded hover:bg-white/5"
                          data-testid={`product-edit-${p.id}`}
                        >
                          {lang === "es" ? "Editar" : "Edita"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit / new dialog */}
      {showDialog && (
        <EditOverlay
          lang={lang}
          editing={editing}
          form={form}
          setForm={setForm}
          saving={saving}
          onClose={closeDialog}
          onSave={save}
          onDelete={remove}
        />
      )}

      {detailId && (
        <ProductDetail
          productId={detailId}
          onClose={() => { setDetailId(null); load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, tint = "" }) {
  return (
    <div className={`card-surface p-4 bg-gradient-to-br ${tint}`}>
      <div className="flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          {label}
        </span>
        <div className="text-slate-300">{icon}</div>
      </div>
      <div className="text-2xl font-bold text-white mt-1 font-mono">{value}</div>
    </div>
  );
}

function EditOverlay({ lang, editing, form, setForm, saving, onClose, onSave, onDelete }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150"
         onClick={onClose}>
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0B1220] border-t sm:border border-white/10 sm:rounded-2xl shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="product-edit-dialog">
        <div className="sticky top-0 z-10 bg-[#0B1220] border-b border-white/10 p-4 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-white">
              {editing
                ? (lang === "es" ? "Editar artículo" : "Edita article")
                : (lang === "es" ? "Nuevo artículo" : "Nou article")}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5 text-slate-400">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "es" ? "Código (SKU/EAN)" : "Codi (SKU/EAN)"}>
              <Input value={form.code} onChange={(e) => set("code", e.target.value)} className="bg-white/5 border-white/10" data-testid="product-code" />
            </Field>
            <Field label={lang === "es" ? "Unidad" : "Unitat"}>
              <select value={form.unit} onChange={(e) => set("unit", e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                      data-testid="product-unit">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
          </div>
          <Field label={lang === "es" ? "Nombre" : "Nom"} required>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" data-testid="product-name" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "es" ? "Categoría" : "Categoria"}>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
            <Field label={lang === "es" ? "Stock mínimo" : "Estoc mínim"}>
              <Input type="number" step="0.01" value={form.min_stock} onChange={(e) => set("min_stock", Number(e.target.value))} className="bg-white/5 border-white/10 font-mono" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "es" ? "Proveedor preferido (NIF)" : "Proveïdor preferit (NIF)"}>
              <Input value={form.default_supplier_nif} onChange={(e) => set("default_supplier_nif", e.target.value)} className="bg-white/5 border-white/10 font-mono" />
            </Field>
            <Field label={lang === "es" ? "Proveedor preferido (nombre)" : "Proveïdor preferit (nom)"}>
              <Input value={form.default_supplier_name} onChange={(e) => set("default_supplier_name", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
          </div>
          <Field label={lang === "es" ? "Precio por defecto" : "Preu per defecte"}>
            <Input type="number" step="0.01" value={form.default_price} onChange={(e) => set("default_price", Number(e.target.value))} className="bg-white/5 border-white/10 font-mono" />
          </Field>
          <Field label={lang === "es" ? "Notas" : "Notes"}>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-white/5 border-white/10" />
          </Field>
        </div>
        <div className="sticky bottom-0 bg-[#0B1220] border-t border-white/10 p-4 flex items-center justify-between gap-2">
          {editing ? (
            <Button variant="ghost" onClick={onDelete} className="text-rose-400 hover:bg-rose-500/10">
              <Trash2 size={14} className="mr-1.5" />
              {lang === "es" ? "Eliminar" : "Elimina"}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {lang === "es" ? "Cancelar" : "Cancel·la"}
            </Button>
            <Button onClick={onSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="product-save-btn">
              <Save size={14} className="mr-1.5" />
              {lang === "es" ? "Guardar" : "Desa"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function PriceAlertsBanner({ alerts, lang, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  if (!alerts || alerts.length === 0) return null;
  return (
    <div
      className="card-surface border-amber-500/30 bg-amber-500/[0.04]"
      data-testid="price-alerts-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid="price-alerts-toggle"
      >
        <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={18} className="text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {lang === "es"
              ? `${alerts.length} alerta(s) de subida de precios`
              : `${alerts.length} alerta(es) de pujada de preus`}
          </div>
          <div className="text-xs text-slate-400">
            {lang === "es"
              ? "Compras donde el precio unitario supera +10% la media histórica del mismo proveedor."
              : "Compres on el preu unitari supera +10% la mitjana històrica del mateix proveïdor."}
          </div>
        </div>
        <span className="text-xs text-amber-300 font-semibold">
          {expanded
            ? (lang === "es" ? "Ocultar" : "Amaga")
            : (lang === "es" ? "Ver" : "Veure")}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-1 border-t border-amber-500/20">
          {alerts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onOpen(a.product_id)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.04] text-left transition-colors"
              data-testid={`price-alert-${a.id}`}
            >
              <TrendingUp size={14} className="text-amber-300 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">
                  {a.product_name || a.description || "—"}
                  <span className="text-[10px] text-slate-500 font-mono ml-2">
                    {a.product_code || ""}
                  </span>
                </div>
                <div className="text-[11px] text-slate-400">
                  {a.supplier_name || a.supplier_nif} ·{" "}
                  {a.occurred_at ? new Date(a.occurred_at).toLocaleDateString("ca-ES") : "—"}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-mono text-sm font-bold text-amber-300">
                  +{a.price_alert_pct}%
                </div>
                <div className="text-[10px] text-slate-400 font-mono">
                  {new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
                    .format(a.unit_price)}{" "}
                  <span className="text-slate-600">vs{" "}
                    {new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
                      .format(a.price_alert_avg)}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
